import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminUsers() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await axios.get(`${API}/admin/users`, { params, headers });
      setUsers(res.data.users);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [page, search, roleFilter, token]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await axios.put(`${API}/admin/users/${userId}`, { active: !currentActive }, { headers });
      fetch();
    } catch (err) { console.error(err); }
  };

  const roleLabels = { admin: 'Administrador', customer: 'Cliente', internal_buyer: 'Comprador Interno' };
  const roleColors = { admin: 'secondary', customer: 'outline', internal_buyer: 'outline' };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-display font-semibold">Usuarios</h1>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Buscar..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
        </div>
        <Select value={roleFilter} onValueChange={v => { setRoleFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Rol" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="admin">Administrador</SelectItem>
            <SelectItem value="customer">Cliente</SelectItem>
            <SelectItem value="internal_buyer">Comprador Interno</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground self-center">{total} usuarios</span>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-secondary/30"><th className="px-3 py-2 text-left font-medium">Nombre</th><th className="px-3 py-2 text-left font-medium">Email</th><th className="px-3 py-2 text-left font-medium">Teléfono</th><th className="px-3 py-2 text-center font-medium">Rol</th><th className="px-3 py-2 text-center font-medium">Estado</th><th className="px-3 py-2 text-center font-medium">Acción</th></tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Cargando...</td></tr>
                ) : users.map(u => (
                  <tr key={u.id} className="border-b hover:bg-secondary/20 transition-colors duration-150">
                    <td className="px-3 py-2 font-medium">{u.name}</td>
                    <td className="px-3 py-2 text-muted-foreground">{u.email}</td>
                    <td className="px-3 py-2">{u.phone || '-'}</td>
                    <td className="px-3 py-2 text-center"><Badge variant={roleColors[u.role] || 'outline'}>{roleLabels[u.role] || u.role}</Badge></td>
                    <td className="px-3 py-2 text-center"><Badge variant={u.active ? 'secondary' : 'destructive'}>{u.active ? 'Activo' : 'Inactivo'}</Badge></td>
                    <td className="px-3 py-2 text-center">
                      <Button variant="ghost" size="sm" onClick={() => handleToggleActive(u.id, u.active)}>{u.active ? 'Desactivar' : 'Activar'}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
          <span className="flex items-center text-sm text-muted-foreground">Página {page} de {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</Button>
        </div>
      )}
    </div>
  );
}
